import { t } from '@procumi/shared';
import type { CategorySpend, OrderedMonth } from '@procumi/shared';
import { useEffect, useRef, useState } from 'react';
import { money, Num, pct, Section, shortMoney } from './shared';

/**
 * Unde merg banii: valoarea comandată pe luni (arie) și pe categorii (inel).
 * Culorile categoriilor sunt ale mărcii, în ordine fixă: ultra, night, lilac, gri.
 * Validatorul de daltonism le desparte bine (ΔE ≥ 25 pe perechile vecine); night
 * și griul nu sunt nuanțe, de aceea numele categoriei stă mereu lângă culoare.
 * Verdele lipsește intenționat, e doar al economiilor.
 */
const SLOTS = ['#2B3BFF', '#070A26', '#9AA3FF', '#5A5F82'];
/** câte categorii au nume în inel; restul intră la „Altele”, ca o culoare să nu se repete */
export const CATEGORY_SLOTS = SLOTS.length;
const OTHER = '#A3A9B5';

function useWidth(min: number, start: number) {
  const box = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(start);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(min, Math.round(e!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [min]);
  return [box, w] as const;
}

/** Valoarea comandată pe ultimele 12 luni; lunile perioadei alese sunt pline. */
export function SpendTrend({
  months,
  from,
  to,
  onAll,
}: {
  months: OrderedMonth[];
  from: string;
  to: string;
  onAll: () => void;
}) {
  const [box, W] = useWidth(260, 560);
  const [hover, setHover] = useState<number | null>(null);
  const H = 200;
  const P = { l: 46, r: 10, t: 12, b: 24 };
  const nice = niceMax(Math.max(...months.map((m) => m.lei), 1));
  const step = (W - P.l - P.r) / Math.max(months.length - 1, 1);
  const x = (i: number) => P.l + i * step;
  const y = (v: number) => P.t + (1 - v / nice) * (H - P.t - P.b);
  const line = months.map((m, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(m.lei).toFixed(1)}`).join(' ');
  const area = `${line} L${x(months.length - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)}Z`;
  const inPeriod = (k: string) => k >= from.slice(0, 7) && k <= to.slice(0, 7);
  const total = months.reduce((n, m) => n + m.lei, 0);
  const h = hover != null ? months[hover] : null;
  return (
    <Section
      icon="trend"
      tone="night"
      title={t('Valoare comandată')}
      sub={t('Din comenzile emise, pe ultimele 12 luni')}
      className="db-spend"
      action={
        <button type="button" className="link" onClick={onAll}>
          {t('Comenzile →')}
        </button>
      }
    >
      <div className="db-spend-tot">
        <b>
          <Num value={total} format={money} />
        </b>
        <span>{t('în 12 luni')}</span>
      </div>
      <div className="db-chart db-area" ref={box} onMouseLeave={() => setHover(null)}>
        {total ? (
          <svg
            viewBox={`0 0 ${W} ${H}`}
            width={W}
            height={H}
            role="img"
            aria-label={t('Valoarea comandată pe luni')}
          >
            <defs>
              <linearGradient id="db-area-g" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#2B3BFF" stopOpacity=".28" />
                <stop offset="1" stopColor="#2B3BFF" stopOpacity=".02" />
              </linearGradient>
            </defs>
            {[0, 0.5, 1].map((f) => (
              <g key={f}>
                <line x1={P.l} x2={W - P.r} y1={y(nice * f)} y2={y(nice * f)} className="grid" />
                <text x={P.l - 8} y={y(nice * f) + 4} textAnchor="end" className="ax">
                  {shortMoney(nice * f)}
                </text>
              </g>
            ))}
            <path d={area} fill="url(#db-area-g)" />
            <path d={line} className="best" />
            {months.map((m, i) => (
              <g key={m.key}>
                <text
                  x={x(i)}
                  y={H - 6}
                  textAnchor="middle"
                  className={`ax${i === hover || inPeriod(m.key) ? ' on' : ''}`}
                >
                  {m.label}
                </text>
                {inPeriod(m.key) || hover === i ? (
                  <circle cx={x(i)} cy={y(m.lei)} r={hover === i ? 5 : 4} className="dot" />
                ) : null}
                <rect
                  x={x(i) - step / 2}
                  y={0}
                  width={step}
                  height={H}
                  fill="transparent"
                  onMouseEnter={() => setHover(i)}
                />
              </g>
            ))}
            {h ? <line x1={x(hover!)} x2={x(hover!)} y1={P.t} y2={H - P.b} className="cross" /> : null}
          </svg>
        ) : (
          <div className="db-empty">{t('Graficul se umple din comenzile emise.')}</div>
        )}
        {h ? (
          <div
            className={`db-tip${hover! > months.length / 2 ? ' left' : ''}`}
            style={{ left: `${(x(hover!) / W) * 100}%` }}
          >
            <b>{h.name}</b>
            <span>
              <i className="best" /> {t('Comandat')} <em>{money(h.lei)}</em>
            </span>
            <small>
              {h.orders} {h.orders === 1 ? t('comandă') : t('comenzi')}
            </small>
          </div>
        ) : null}
      </div>
    </Section>
  );
}

/** Inelul pe categorii, cu legenda alături: culoare, nume, sumă, procent. */
export function CategoryDonut({ rows, rangeLabel }: { rows: CategorySpend[]; rangeLabel: string }) {
  const [on, setOn] = useState<number | null>(null);
  const total = rows.reduce((n, r) => n + r.lei, 0);
  const color = (r: CategorySpend, i: number) => (r.category == null ? OTHER : (SLOTS[i] ?? OTHER));
  const R = 70;
  const T = 22;
  const C = 2 * Math.PI * R;
  const gap = rows.length > 1 ? 2.5 : 0;
  let acc = 0;
  return (
    <Section
      icon="pie"
      tone="blue"
      title={t('Pe categorii')}
      sub={t('Din comenzile emise, {0}', rangeLabel.toLowerCase())}
      className="db-cats"
    >
      {total ? (
        <div className="db-donut">
          <svg
            viewBox="0 0 180 180"
            width="180"
            height="180"
            role="img"
            aria-label={t('Valoarea comandată pe categorii')}
          >
            <g transform="rotate(-90 90 90)">
              {rows.map((r, i) => {
                const len = r.share * C;
                const dash = Math.max(len - gap, 0.5);
                const el = (
                  <circle
                    key={r.category ?? 'other'}
                    cx="90"
                    cy="90"
                    r={R}
                    fill="none"
                    stroke={color(r, i)}
                    strokeWidth={on === i ? T + 6 : T}
                    strokeDasharray={`${dash} ${C - dash}`}
                    strokeDashoffset={-acc}
                    opacity={on == null || on === i ? 1 : 0.35}
                    onMouseEnter={() => setOn(i)}
                    onMouseLeave={() => setOn(null)}
                  />
                );
                acc += len;
                return el;
              })}
            </g>
            <text x="90" y="86" textAnchor="middle" className="db-donut-v">
              {shortMoney(on == null ? total : rows[on]!.lei)}
            </text>
            <text x="90" y="104" textAnchor="middle" className="db-donut-l">
              {on == null ? t('total lei') : pct(rows[on]!.share)}
            </text>
          </svg>
          <ul className="db-donut-k">
            {rows.map((r, i) => (
              <li
                key={r.category ?? 'other'}
                className={on === i ? 'on' : undefined}
                onMouseEnter={() => setOn(i)}
                onMouseLeave={() => setOn(null)}
              >
                <i style={{ background: color(r, i) }} />
                <b>{r.category ?? t('Altele')}</b>
                <span>{money(r.lei)}</span>
                <em>{pct(r.share)}</em>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="db-empty">{t('Nicio comandă emisă în perioadă.')}</div>
      )}
    </Section>
  );
}

function niceMax(v: number): number {
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
}
